<script lang="ts">
  // BJL-CHECK: Button (used), Spinner (used), Badge, Tag, Drawer, List. Same shell as
  // BusStopSheet and for the same reasons (see artifacts/deviations/BusStopSheet.md): the
  // drag-resizable sheet is something Drawer can't express, and a route badge is painted in
  // the operator's colour — data, not a theme token — so it is RouteBadge, not a Tag variant.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Colors, getRouteLongName } from '../../ui/colors'
  import { cardChrome, panelFrame, ROW_RULE, Type } from '../../ui/theme'
  import { safeBottom } from '../../ui/safeArea'
  import { formatCountdown, formatShortClock } from '../../ui/transitTime'
  import { useBackHandler } from '../../hooks/useBackHandler.svelte'
  import { useFerryStore } from '../../store/ferry'
  import { api, type FerryDeparture, type FerryDepartureGroup } from '../../services/api'
  import { storeSelect } from '../../lib/store.svelte'
  import { useResizableSheet } from '../../lib/resizableSheet.svelte'
  import type { TransferContext } from './timetable'
  import type { FerryLandingInfo } from './stationInfo'
  import TripTimetable from './TripTimetable.svelte'
  import TransferBanner from './TransferBanner.svelte'
  import RouteBadge from '../RouteBadge.svelte'
  import SectionEyebrow from '../SectionEyebrow.svelte'
  import SheetBackButton from '../SheetBackButton.svelte'

  interface Props {
    landing: FerryLandingInfo
    onClose: () => void
    /** Reopen the sheet on another landing, chosen from a boat's timetable. */
    onTransfer?: (landing: FerryLandingInfo, ctx: TransferContext | null) => void
    /** Set when reached by transferring: departures before the rider gets here are hidden. */
    transfer?: TransferContext | null
  }

  const { landing, onClose, onTransfer, transfer }: Props = $props()

  const _wh = typeof window !== 'undefined' ? window.innerHeight : 800
  const sheet = useResizableSheet({
    minHeight: 80,
    defaultHeight: Math.round(_wh * 0.45),
    maxHeight: Math.round(_wh * 0.92),
  })

  // Ferries run every 20–60 minutes, so the overview looks three hours out (the backend's
  // default) where the subway board looks one; a direction's own list looks out the rest
  // of the service day.
  const DETAIL_HOURS = 12
  // The feed republishes every ~30 s; polling faster only re-reads the same snapshot.
  const REFRESH_MS = 30_000

  const network = storeSelect(useFerryStore, (s) => s.network)
  const landingRoutes = $derived(
    network.value?.landings.find((l) => l.id === landing.id)?.routes ?? [],
  )
  const accessible = $derived(network.value?.landings.find((l) => l.id === landing.id)?.accessible)

  let groups = $state<FerryDepartureGroup[] | null>(null)
  let detail = $state<FerryDepartureGroup[] | null>(null)
  let loading = $state(true)
  let failed = $state(false)
  // route|direction|headsign — one direction of one route, as the backend groups it.
  let selectedKey = $state<string | null>(null)
  let selectedTrip = $state<{ tripId: string; routeId: string; headsign: string } | null>(null)

  useBackHandler(() => selectedKey !== null, () => { selectedKey = null })
  useBackHandler(() => selectedTrip !== null, () => { selectedTrip = null })

  const keyOf = (g: FerryDepartureGroup) => `${g.routeId}|${g.directionId}|${g.headsign}`
  const landingId = $derived(landing.id)

  let bodyEl = $state<HTMLDivElement | null>(null)
  const drillKey = $derived(
    `${landingId}|${selectedTrip ? `trip:${selectedTrip.tripId}` : selectedKey ? `dir:${selectedKey}` : 'overview'}`,
  )
  $effect(() => {
    drillKey
    if (bodyEl) bodyEl.scrollTop = 0
  })

  $effect(() => {
    const id = landingId
    let live = true
    const load = () => {
      api
        .getFerryDepartures(id)
        .then((d) => { if (live) { groups = d.groups; failed = false } })
        .catch(() => { if (live) { groups = groups ?? []; failed = true } })
        .finally(() => { if (live) loading = false })
    }
    loading = true
    groups = null
    detail = null
    selectedKey = null
    selectedTrip = null
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  // The longer look-ahead, only while a direction is open.
  $effect(() => {
    const id = landingId
    if (!selectedKey) return
    let live = true
    const load = () => {
      api
        .getFerryDepartures(id, DETAIL_HOURS)
        .then((d) => { if (live) detail = d.groups })
        .catch(() => {})
    }
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  })

  // A transferring rider can see everything with one tap; a fresh transfer re-arms it.
  let showAll = $state(false)
  $effect(() => {
    transfer
    showAll = false
  })
  const cutoff = $derived(transfer && !showAll ? transfer.arriveAt : null)

  const catchable = (list: FerryDeparture[]) =>
    cutoff == null ? list : list.filter((d) => d.time >= cutoff)

  const visibleGroups = $derived(
    (groups ?? [])
      .map((g) => ({ ...g, departures: catchable(g.departures) }))
      .filter((g) => g.departures.length > 0),
  )
  const selectedGroup = $derived.by(() => {
    if (!selectedKey) return null
    const g = (detail ?? groups ?? []).find((x) => keyOf(x) === selectedKey)
    return g ? { ...g, departures: catchable(g.departures) } : null
  })

  /** "On time", "3 min late" or "Scheduled": what the number beside it is standing on. */
  function status(d: FerryDeparture): { text: string; color: string } {
    if (!d.realtime) return { text: 'Scheduled', color: Colors.textMuted }
    const late = Math.round((d.time - d.scheduledTime) / 60)
    return late >= 2
      ? { text: `${late} min late`, color: Colors.danger }
      : { text: 'Live · on time', color: Colors.success }
  }

  function openTrip(g: FerryDepartureGroup, d: FerryDeparture) {
    selectedTrip = { tripId: d.tripId, routeId: g.routeId, headsign: g.headsign }
  }
</script>

{#snippet departureTime(d: FerryDeparture, big: boolean)}
  {@const label = formatCountdown(d.time, { fallback: '--' })}
  <!-- A countdown under an hour, a clock time beyond it (and always on a transfer, where
       "in 40 min" from now means nothing to a rider who won't be here for 30). -->
  {#if cutoff == null && d.time - Date.now() / 1000 < 3600}
    {@const isNow = label === 'Now'}
    <span style="color: {isNow ? Colors.success : Colors.text}; {big ? Type.screen : Type.title};">
      {isNow ? label : label.replace(' min', '')}
    </span>
    {#if !isNow}<span style="color: {Colors.textSecondary}; {Type.caption};"> min</span>{/if}
  {:else}
    <span style="color: {Colors.text}; {Type.title};">{formatShortClock(d.time)}</span>
  {/if}
{/snippet}

<div
  style="
    position: fixed; bottom: 0; left: 0; right: 0;
    height: {sheet.height}px;
    background-color: {Colors.ground};
    {panelFrame('top')}
    z-index: 1000;
    display: flex; flex-direction: column;
    transition: {sheet.dragging ? 'none' : 'height 0.22s cubic-bezier(0.4,0,0.2,1)'};
  "
>
  <!-- Handle row with the chrome on it, laid out exactly as the bus-stop sheet's. -->
  <div style="position: relative; flex-shrink: 0; display: flex; align-items: center; padding: 4px 18px 2px;">
    <div style="display: flex; align-items: center; flex-shrink: 0;">
      {#if selectedTrip}
        <SheetBackButton onclick={() => { selectedTrip = null }} label="Back to departures" />
      {:else if selectedKey}
        <SheetBackButton onclick={() => { selectedKey = null }} label="Back to all routes" />
      {/if}
    </div>
    <div
      onpointerdown={sheet.onHandleDown}
      role="separator"
      aria-label="Resize ferry landing panel"
      style="flex: 1; align-self: stretch; min-height: 34px; cursor: ns-resize; user-select: none; touch-action: none;"
    ></div>
    <div
      style="
        position: absolute; left: 50%; top: 14px; transform: translateX(-50%);
        width: 36px; height: 4px; border-radius: 2px;
        background-color: rgba(255,255,255,0.25); pointer-events: none;
      "
    ></div>
    <Button variant="text" size="sm" iconOnly onclick={onClose} aria-label="Close ferry landing panel">✕</Button>
  </div>

  <div style="padding: 4px 20px 0; flex-shrink: 0;">
    <div style="color: {Colors.textMuted}; {Type.caption};">
      NYC Ferry landing{accessible ? ' · ♿ Accessible' : ''}
    </div>
    <div style="color: {Colors.text}; {Type.heading}; margin-top: 2px; overflow-wrap: anywhere;">
      {landing.name}
    </div>
  </div>

  {#if !selectedTrip && !selectedKey && landingRoutes.length > 0}
    <div style="display: flex; flex-wrap: wrap; gap: 8px; padding: 12px 20px 0; flex-shrink: 0;">
      {#each landingRoutes as routeId (routeId)}
        <div style="display: flex; align-items: center; gap: 6px;">
          <RouteBadge {routeId} size={24} />
          <span style="color: {Colors.textSecondary}; {Type.caption};">{getRouteLongName(routeId)}</span>
        </div>
      {/each}
    </div>
  {/if}

  <div bind:this={bodyEl} style="overflow-y: auto; flex: 1; min-height: 0; padding: 0 20px {safeBottom(28)};">
    {#if selectedTrip}
      <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 10px;">
        <RouteBadge routeId={selectedTrip.routeId} size={32} />
        <span style="flex: 1; min-width: 0; color: {Colors.text}; {Type.title}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
          To {selectedTrip.headsign}
        </span>
      </div>
      <TripTimetable
        source={{ kind: 'ferry', tripId: selectedTrip.tripId }}
        routeId={selectedTrip.routeId}
        currentStopId={landing.id}
        onTransfer={(stopId, stopName, ctx) => onTransfer?.({ id: stopId, name: stopName }, ctx)}
      />
    {:else if loading && !groups}
      <div style="display: flex; align-items: center; gap: 8px; padding: 10px 0; color: {Colors.textMuted}; {Type.body};">
        <Spinner size="sm" label="Loading ferries" /> Loading ferries…
      </div>
    {:else if selectedKey}
      {#if selectedGroup}
        <div style="display: flex; align-items: center; gap: 10px; padding: 4px 0 8px;">
          <RouteBadge routeId={selectedGroup.routeId} size={32} />
          <span style="flex: 1; min-width: 0; color: {Colors.text}; {Type.title};">To {selectedGroup.headsign}</span>
        </div>
        <SectionEyebrow
          text="Later today · {selectedGroup.departures.length} boat{selectedGroup.departures.length === 1 ? '' : 's'}"
          marginBottom={2}
        />
        <div style="display: flex; flex-direction: column;">
          {#each selectedGroup.departures as d (d.tripId + d.time)}
            {@const st = status(d)}
            <button
              onclick={() => openTrip(selectedGroup, d)}
              aria-label="See the timetable for the {formatShortClock(d.time)} boat"
              style="
                display: flex; align-items: center; gap: 10px; padding: 10px 0;
                background: none; border: none; border-bottom: {ROW_RULE};
                cursor: pointer; text-align: left; width: 100%;
              "
            >
              <div style="flex: 1; min-width: 0;">
                <div style="color: {Colors.text}; {Type.body}; font-weight: 600;">
                  Departs {formatShortClock(d.time)}
                </div>
                <div style="color: {st.color}; {Type.caption}; margin-top: 2px;">
                  {st.text}{d.vesselLabel ? ` · Vessel ${d.vesselLabel}` : ''}
                </div>
              </div>
              <div style="text-align: right; flex-shrink: 0;">{@render departureTime(d, false)}</div>
              <span aria-hidden="true" style="color: {Colors.textSecondary}; font-size: 18px;">›</span>
            </button>
          {/each}
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          No more boats this way today.
        </div>
      {/if}
    {:else}
      {#if transfer}
        <TransferBanner ctx={transfer} active={!showAll} onToggle={() => { showAll = !showAll }} />
      {/if}
      {#if visibleGroups.length > 0}
        <!-- One row per route + direction: where the boat goes, the next departure large on
             the trailing edge, and the two after it — at 20–60 minute headways the NEXT
             boat alone doesn't tell a rider whether to run for it. -->
        <div style="{cardChrome()} padding: 0 14px; margin-top: 16px;">
          {#each visibleGroups as g, i (keyOf(g))}
            {@const next = g.departures[0]}
            {@const st = status(next)}
            <button
              onclick={() => { selectedKey = keyOf(g) }}
              aria-label="See later {getRouteLongName(g.routeId) || 'ferries'} boats to {g.headsign}"
              style="
                display: flex; align-items: center; gap: 12px; padding: 12px 0;
                background: none; border: none;
                {i === visibleGroups.length - 1 ? '' : `border-bottom: ${ROW_RULE};`}
                cursor: pointer; text-align: left; width: 100%;
              "
            >
              <RouteBadge routeId={g.routeId} size={30} />
              <div style="flex: 1; min-width: 0;">
                <div style="color: {Colors.text}; {Type.bodyLg}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  To {g.headsign}
                </div>
                <div style="color: {Colors.textMuted}; {Type.caption}; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <span style="color: {st.color};">{st.text}</span>
                  {#if g.departures.length > 1}
                    · then {g.departures.slice(1, 3).map((d) => formatShortClock(d.time)).join(', ')}
                  {/if}
                </div>
              </div>
              <div style="text-align: right; flex-shrink: 0;">{@render departureTime(next, true)}</div>
              <span aria-hidden="true" style="flex-shrink: 0; color: rgba(255,255,255,0.35); font-size: 15px;">›</span>
            </button>
          {/each}
        </div>
      {:else}
        <div style="color: {Colors.textMuted}; {Type.body}; padding: 10px 0;">
          {failed
            ? 'Couldn’t reach NYC Ferry’s timetable. Retrying…'
            : cutoff != null
              ? `No boats leave here after ${formatShortClock(cutoff)} in the next few hours.`
              : 'No boats leave here in the next few hours.'}
        </div>
      {/if}
    {/if}
  </div>
</div>
